import React, { useEffect, useState } from "react";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { studentHasEhcp } from "../lib/students";
import {
  getAcademicYearLabel,
  resolveKeyStage,
  frameworkForKeyStage,
  normalizeSubjectAttainment,
  buildSubjectSummary,
  isAtExpectedOverall,
  FRAMEWORK_LABELS,
  TERM_LABELS,
  getLatestTermSubjects,
} from "../lib/curriculumAssessment";
import { formatStudentDate } from "../lib/formatStudentDate";

function formatDate(value) {
  return formatStudentDate(value, { missing: 'Not recorded' });
}

export default function StudentOverview({ student, activeInterventions = [], hideIdentityHeader = false }) {
  const [attainmentSummary, setAttainmentSummary] = useState(null);
  const yearLabel = getAcademicYearLabel();

  useEffect(() => {
    const fetchAttainment = async () => {
      try {
        const ref = doc(db, 'students', student.id, 'attainmentRecords', yearLabel);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          const keyStage = data.keyStage || resolveKeyStage(student.yearGroup, student.phase);
          const framework = data.assessmentFramework || frameworkForKeyStage(keyStage);
          const subjectAttainment = normalizeSubjectAttainment(data, keyStage);
          const { term } = getLatestTermSubjects(subjectAttainment, keyStage);
          setAttainmentSummary({
            framework,
            keyStage,
            term,
            subjects: buildSubjectSummary(subjectAttainment, keyStage, framework),
            atExpectedOverall: isAtExpectedOverall(subjectAttainment, keyStage, framework),
          });
        } else {
          setAttainmentSummary(null);
        }
      } catch (e) {
        setAttainmentSummary(null);
      }
    };
    if (student?.id) fetchAttainment();
  }, [student?.id, student?.yearGroup, student?.phase, yearLabel]);

  if (!student) return <p>Loading...</p>;

  const hasEhcp = studentHasEhcp(student);

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6">
      {/* Identity Header (optional) */}
      {!hideIdentityHeader && (
        <Card>
          <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-md p-6">
            <h1 className="text-3xl font-bold">{student.firstName} {student.lastName}</h1>
            <p className="text-lg">{student.yearGroup} • {student.phase}</p>
            <Badge className="mt-2 bg-green-100 text-green-800 border-green-200">
              {hasEhcp ? 'Has EHCP' : 'No EHCP'}
            </Badge>
          </CardHeader>
        </Card>
      )}

      {/* Overview Section */}
      <Card>
        <CardHeader>
          <CardTitle>Key Information</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Date of Birth</p>
            <p className="text-base text-gray-900">{formatDate(student.dateOfBirth)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Year Group</p>
            <p className="text-base text-gray-900">{student.yearGroup}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Phase</p>
            <p className="text-base text-gray-900">{student.phase}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Academic Level</p>
            <p className="text-base text-gray-900">{student.academicLevel}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Attendance</p>
            <p className="text-base text-gray-900">{student.attendance}%</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-sm text-gray-500">Curriculum attainment (this academic year)</p>
            {attainmentSummary ? (
              <div className="mt-2 space-y-2">
                <div className="flex flex-wrap gap-2 items-center">
                  <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200">
                    {FRAMEWORK_LABELS[attainmentSummary.framework]}
                  </Badge>
                  {attainmentSummary.term && (
                    <span className="text-xs text-gray-500">
                      Latest: {TERM_LABELS[attainmentSummary.term]}
                    </span>
                  )}
                  <Badge
                    variant="outline"
                    className={
                      attainmentSummary.atExpectedOverall
                        ? 'bg-green-50 text-green-800 border-green-200'
                        : 'bg-amber-50 text-amber-800 border-amber-200'
                    }
                  >
                    {attainmentSummary.atExpectedOverall ? 'At expected (all core subjects)' : 'Not yet at expected (all core)'}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  {attainmentSummary.subjects.map(({ subject, level, atExpected }) => (
                    <Badge
                      key={subject}
                      variant="outline"
                      className={
                        atExpected
                          ? 'bg-green-50 text-green-800 border-green-200'
                          : 'bg-blue-50 text-blue-800 border-blue-200'
                      }
                    >
                      {subject}: {level}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : (
              <p className="mt-1 text-sm text-gray-500">No attainment recorded yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Special Needs Section */}
      <Card>
        <CardHeader>
          <CardTitle>Special Needs</CardTitle>
        </CardHeader>
        <CardContent>
          {student.specialNeeds && student.specialNeeds.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {student.specialNeeds.map((need, index) => (
                <Badge key={index} variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
                  {need}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No special needs recorded</p>
          )}
        </CardContent>
      </Card>

      {/* EHCP Details Section */}
      {hasEhcp && student.ehcpDetails && (
        <Card>
          <CardHeader>
            <CardTitle>EHCP Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Needs</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {(student.ehcpDetails.needs || []).length > 0 ? (
                  (student.ehcpDetails.needs || []).map((need, index) => (
                    <Badge key={index} variant="outline" className="bg-red-100 text-red-800 border-red-200">
                      {need}
                    </Badge>
                  ))
                ) : (
                  <p className="text-gray-500">None recorded</p>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500">Targets</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {(student.ehcpDetails.targets || []).length > 0 ? (
                  (student.ehcpDetails.targets || []).map((target, index) => (
                    <Badge key={index} variant="outline" className="bg-green-100 text-green-800 border-green-200">
                      {target}
                    </Badge>
                  ))
                ) : (
                  <p className="text-gray-500">None recorded</p>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500">Last Review</p>
              <p className="text-base text-gray-900">{formatDate(student.ehcpDetails.lastReview)}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Intervention Groups Section */}
      <Card>
        <CardHeader>
          <CardTitle>Intervention Groups</CardTitle>
        </CardHeader>
        <CardContent>
          {activeInterventions && activeInterventions.length > 0 ? (
            <div>
              <p className="text-sm text-gray-600 mb-2">Active interventions</p>
              <div className="flex flex-wrap gap-2">
                {activeInterventions.map((i) => (
                  <Badge key={i.id} variant="outline" className="bg-purple-100 text-purple-800 border-purple-200">
                    {i.name || 'Intervention'}
                  </Badge>
                ))}
              </div>
            </div>
          ) : student.interventionGroups && student.interventionGroups.length > 0 ? (
            <div>
              <p className="text-sm text-gray-600 mb-2">Assigned groups</p>
              <div className="flex flex-wrap gap-2">
                {student.interventionGroups.map((group, index) => (
                  <Badge key={index} variant="outline" className="bg-purple-100 text-purple-800 border-purple-200">
                    {group}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No intervention groups assigned</p>
          )}
        </CardContent>
      </Card>
      {student.curriculumPlan && (
  <div className="mt-6">
    <h3 className="text-lg font-bold">Saved Curriculum</h3>
    <p className="whitespace-pre-line mt-2 bg-gray-100 p-4 rounded">
      {student.curriculumPlan}
    </p>
  </div>
)}
    </div>
  );
}
